import React from 'react';
import PropTypes from 'prop-types';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useReporter } from '../../reporter';
import { Button, LinkButton } from '../../atoms';
import {
  BillingInformationForm,
  BillingInformationSchema,
} from '../../components';
import { ApiResult } from '../../api';
import { PAYMENT_ERROR_MESSAGE } from '../constants';
import { clickUpdatePayment } from './events';
import { ButtonContainer } from './styles';

export const EditPaymentForm = ({
  labelWithStripe,
  labelNameCard,
  labelCardNumber,
  labelExpirationDate,
  labelCVC,
  placeholderNameCard,
  placeholderCardNumber,
  placeholderExpirationDate,
  placeholderCVC,
  saveBtnLabel,
  cancelBtnLabel,
  onSubmit,
  cancelBtnTarget,
  isSubmitting,
}) => {
  const reporter = useReporter();

  const methods = useForm({
    defaultValues: {
      name: '',
      cardNumber: '',
      cardExpiry: '',
      cardCvc: '',
    },
    resolver: yupResolver(BillingInformationSchema),
    mode: 'onChange',
  });

  const onFormSubmit = async () => {
    reporter.tag(clickUpdatePayment());
    const result = await onSubmit();
    ApiResult.match(result, {
      error: {
        payment: () => {
          methods.setError(
            'cardNumber',
            {
              type: 'payment',
              message: PAYMENT_ERROR_MESSAGE,
            },
            { shouldFocus: true },
          );
        },
      },
    });
    ApiResult.ifError(result, () =>
      setTimeout(() =>
        methods.reset(methods.getValues(), {
          keepIsSubmitted: false,
          keepErrors: true,
        }),
      ),
    );
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onFormSubmit)}>
        <BillingInformationForm
          labelWithStripe={labelWithStripe}
          labelNameCard={labelNameCard}
          labelCardNumber={labelCardNumber}
          labelExpirationDate={labelExpirationDate}
          labelCVC={labelCVC}
          placeholderNameCard={placeholderNameCard}
          placeholderCardNumber={placeholderCardNumber}
          placeholderExpirationDate={placeholderExpirationDate}
          placeholderCVC={placeholderCVC}
        />
        <ButtonContainer>
          <Button fullWidth disabled={isSubmitting} type="submit">
            {saveBtnLabel}
          </Button>
          <LinkButton fullWidth href={cancelBtnTarget} variant="secondary">
            {cancelBtnLabel}
          </LinkButton>
        </ButtonContainer>
      </form>
    </FormProvider>
  );
};

EditPaymentForm.displayName = 'EditPaymentForm';
EditPaymentForm.propTypes = {
  labelWithStripe: PropTypes.string.isRequired,
  labelNameCard: PropTypes.string.isRequired,
  labelCardNumber: PropTypes.string.isRequired,
  labelExpirationDate: PropTypes.string.isRequired,
  labelCVC: PropTypes.string.isRequired,
  placeholderNameCard: PropTypes.string.isRequired,
  placeholderCardNumber: PropTypes.string.isRequired,
  placeholderExpirationDate: PropTypes.string.isRequired,
  placeholderCVC: PropTypes.string.isRequired,
  saveBtnLabel: PropTypes.string.isRequired,
  cancelBtnLabel: PropTypes.string.isRequired,
  onSubmit: PropTypes.func.isRequired,
  cancelBtnTarget: PropTypes.string.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
};
